import {
    InscalarProvider as Provider,
    useInscalar,
} from '@ilg-core/inscalar-web/react'
import { useInscalarIdentity } from '@ilg-core/inscalar-web/react'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import { InscalarSDK } from '@ilg-core/inscalar-web'
import type { InscalarConfig, LogAdapter } from '@ilg-core/inscalar-web'
import { captureException } from '@sentry/nextjs'
import Cookies from 'js-cookie'

import { INSCALAR_SESSION_ID_COOKIE_NAME } from '@config/cookies'

import { SENTRY_TAG_INSCALAR } from '@constants/sentryTags'

import useAppQueryState from '@hooks/useAppQueryState'

import { getInscalarAppEnv } from '@utils/getInscalarAppEnv'
import { getCookieYesConsent, getHasAnalyticsConsent } from '@utils/inscalar'

const logAdapter: LogAdapter = {
    debug: (message: string, context) => {
        console.debug(`[InscalarSDK] ${message}`, context ?? '')
    },
    info: (message: string, context) => {
        console.info(`[InscalarSDK] ${message}`, context ?? '')
    },
    warn: (message: string, context) => {
        console.warn(`[InscalarSDK] ${message}`, context ?? '')
    },
    error: (message: string, context) => {
        console.error(`[InscalarSDK] ${message}`, context ?? '')
    },
}

const toError = (serialized: unknown): Error | undefined => {
    if (serialized instanceof Error) return serialized
    if (!serialized || typeof serialized !== 'object') return undefined
    const { name, message, stack } = serialized as {
        message?: string
        name?: string
        stack?: string
    }
    const error = new Error(message ?? 'Unknown error')
    if (name) error.name = name
    if (stack) error.stack = stack
    return error
}

const inscalarConfig: InscalarConfig = {
    projectId: process.env.NEXT_PUBLIC_INSCALAR_PROJECT_ID ?? '',
    apiKey: process.env.NEXT_PUBLIC_INSCALAR_API_KEY ?? '',
    environment: getInscalarAppEnv(),
    batchSize: 1,
    apiBaseUrl: process.env.NEXT_PUBLIC_INSCALAR_API_BASE_URL ?? '',
    logAdapter,
    onError: (error, context) => {
        if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
            return
        }
        captureException(toError(context?.error) ?? error, {
            tags: { [SENTRY_TAG_INSCALAR]: true },
            extra: {
                sdkErrorMessage: error.message,
                ...context,
            },
        })
    },
    debug: process.env.NEXT_PUBLIC_APP_ENV !== 'production',
}

function InscalarConsentSync({ children }: PropsWithChildren) {
    const { isInitialized } = useInscalar()
    const [isConsentSynced, setIsConsentSynced] = useState(false)

    useEffect(() => {
        if (!isInitialized) return

        const syncConsent = async () => {
            const consent = getCookieYesConsent()

            if (!consent) {
                if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
                    await InscalarSDK.setConsent(true, '2.4')
                } else {
                    console.warn('No consent')
                }

                setIsConsentSynced(true)
                return
            }

            const hasConsent = getHasAnalyticsConsent(consent)
            await InscalarSDK.setConsent(hasConsent, '2.4')

            setIsConsentSynced(true)
        }

        syncConsent()

        window.addEventListener('cookieyes_consent_update', syncConsent)

        return () => {
            window.removeEventListener('cookieyes_consent_update', syncConsent)
        }
    }, [isInitialized])

    if (!isConsentSynced) return null

    return <>{children}</>
}

function InscalarSessionId() {
    const { isInitialized } = useInscalar()
    const { sessionId } = useInscalarIdentity()

    useEffect(() => {
        if (!isInitialized || !sessionId) return

        // Cookies to set for inscalar session id
        Cookies.set(INSCALAR_SESSION_ID_COOKIE_NAME, sessionId, {
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'lax',
        })
    }, [isInitialized, sessionId])

    return null
}

function InscalarIdentity() {
    const { appQueryState } = useAppQueryState()
    const { isInitialized } = useInscalar()
    const { identify } = useInscalarIdentity()
    const hasRun = useRef(false)

    useEffect(() => {
        if (!isInitialized || hasRun.current) return

        hasRun.current = true

        if (appQueryState.customerId) {
            identify(appQueryState.customerId, {
                userIdentifiedEventVersion: '1.4',
                profileAliasEventVersion: '2.4',
            })
        }
    }, [isInitialized, appQueryState.customerId, identify])

    return null
}

export const InscalarProvider = ({ children }: PropsWithChildren) => {
    return (
        <Provider config={inscalarConfig}>
            <InscalarSessionId />
            <InscalarConsentSync>
                <InscalarIdentity />
            </InscalarConsentSync>
            {children}
        </Provider>
    )
}
